import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "@/components/Logo";
import { api, apiErr } from "@/lib/api";
import { parseCSV, FIELDS, autoMap } from "@/lib/csv";
import { CATEGORIES, LAYOUTS, THEMES } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, ArrowLeft, ArrowRight, X, FileText, Loader2 } from "lucide-react";

const SAMPLE = `Date,Title,Description,Category
1440,Printing Press,Gutenberg develops movable type in Mainz.,Invention
1876,Telephone,Alexander Graham Bell patents the telephone.,Invention
1969,Internet,ARPANET sends its first message.,Technology
2007,Smartphone,The iPhone launches and reshapes computing.,Technology`;

export default function Import() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [raw, setRaw] = useState("");
  const [parsed, setParsed] = useState({ headers: [], data: [] });
  const [map, setMap] = useState({});
  const [meta, setMeta] = useState({ title: "Imported Timeline", category: "Custom", layout: "vertical", theme: "aurora" });
  const [busy, setBusy] = useState(false);

  const doParse = (text) => {
    const p = parseCSV(text);
    if (p.data.length === 0) { toast.error("Couldn't find any rows. Check your CSV."); return; }
    setParsed(p); setMap(autoMap(p.headers)); setStep(1);
  };

  const onFile = async (file) => {
    const text = await file.text();
    setRaw(text); doParse(text);
  };

  const events = parsed.data.map((row) => {
    const get = (k) => (map[k] ? row[map[k]] : "");
    const rawYear = get("date");
    const s = String(rawYear);
    let yearNum = parseInt(s.replace(/[^0-9-]/g, ""), 10);
    if (/\b(bce|bc)\b/i.test(s) && yearNum > 0) yearNum = -yearNum;
    return {
      date: rawYear, year: Number.isNaN(yearNum) ? undefined : yearNum,
      title: get("title"), description: get("description"),
      image: get("image"), category: get("category"), url: get("url"),
    };
  });

  const create = async () => {
    if (!map.date || !map.title) { toast.error("Map at least Date and Title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/timelines/import", { ...meta, events });
      toast.success(`Imported ${events.length} events`);
      nav(`/editor/${data.id}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <header className="h-16 flex items-center justify-between px-5 sm:px-8 border-b border-border glass sticky top-0 z-40">
        <Logo />
        <Link to="/dashboard"><Button variant="ghost" size="icon" data-testid="import-close"><X size={20} /></Button></Link>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-12">
        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Import Data</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Bring your data in</h1>
              <p className="mt-2 text-muted-foreground">Upload a CSV/spreadsheet export, or paste rows below. You'll map columns to timeline fields next.</p>

              <label className="mt-8 flex flex-col items-center justify-center gap-3 h-40 rounded-2xl border-2 border-dashed border-border cursor-pointer hover:border-primary/40 transition-colors">
                <div className="h-12 w-12 grid place-items-center rounded-xl bg-accent text-primary"><Upload size={22} /></div>
                <div className="text-sm font-medium text-foreground">Click to upload a CSV file</div>
                <div className="text-xs text-muted-foreground">.csv or .txt</div>
                <input type="file" accept=".csv,.txt,text/csv" className="hidden" data-testid="import-file" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
              </label>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-muted-foreground">Or paste CSV data</Label>
                  <button onClick={() => setRaw(SAMPLE)} className="text-xs text-primary hover:underline">Use sample</button>
                </div>
                <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="import-paste"
                  placeholder={"Date,Title,Description,Category\n1969,Apollo 11,First Moon landing,Space"} className="min-h-[140px] font-mono text-xs" />
                <Button onClick={() => doParse(raw)} disabled={!raw.trim()} className="mt-4 rounded-full" data-testid="import-parse-btn"><FileText size={16} className="mr-1.5" /> Parse data</Button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <button onClick={() => setStep(0)} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft size={15} /> Back</button>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Map columns</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Match your columns</h1>
              <p className="mt-2 text-muted-foreground">We found {parsed.data.length} rows and {parsed.headers.length} columns. Map them to timeline fields.</p>

              <div className="mt-8 space-y-3">
                {FIELDS.map((f) => (
                  <div key={f.key} className="flex items-center gap-4">
                    <div className="w-32 shrink-0 text-sm font-medium text-foreground">{f.label}{f.required && <span className="text-primary">*</span>}</div>
                    <Select value={map[f.key] || "__none"} onValueChange={(v) => setMap((m) => ({ ...m, [f.key]: v === "__none" ? undefined : v }))}>
                      <SelectTrigger className="flex-1" data-testid={`map-${f.key}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">— Not mapped —</SelectItem>
                        {parsed.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-xl border border-border overflow-hidden">
                <div className="px-4 py-2 bg-muted text-xs uppercase tracking-widest text-muted-foreground">Preview</div>
                <div className="divide-y divide-border max-h-56 overflow-y-auto">
                  {events.slice(0, 5).map((e, idx) => (
                    <div key={idx} className="px-4 py-3">
                      <div className="text-xs font-semibold text-primary">{e.date || "—"}</div>
                      <div className="text-sm font-medium text-foreground">{e.title || "Untitled"}</div>
                      {e.description && <div className="text-xs text-muted-foreground line-clamp-1">{e.description}</div>}
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={() => setStep(2)} disabled={!map.date || !map.title} className="mt-8 rounded-full" data-testid="import-next-btn">Continue <ArrowRight size={16} className="ml-1" /></Button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <button onClick={() => setStep(1)} className="text-sm text-muted-foreground flex items-center gap-1 mb-6 hover:text-foreground"><ArrowLeft size={15} /> Back</button>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Finishing touches</div>
              <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Name your timeline</h1>
              <div className="mt-8 space-y-5">
                <div><Label className="text-xs text-muted-foreground mb-1.5 block">Title</Label><Input value={meta.title} onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))} className="h-11" data-testid="import-title" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs text-muted-foreground mb-1.5 block">Category</Label>
                    <Select value={meta.category} onValueChange={(v) => setMeta((m) => ({ ...m, category: v }))}><SelectTrigger data-testid="import-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                  </div>
                  <div><Label className="text-xs text-muted-foreground mb-1.5 block">Layout</Label>
                    <Select value={meta.layout} onValueChange={(v) => setMeta((m) => ({ ...m, layout: v }))}><SelectTrigger data-testid="import-layout"><SelectValue /></SelectTrigger><SelectContent>{LAYOUTS.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div><Label className="text-xs text-muted-foreground mb-1.5 block">Theme</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {Object.entries(THEMES).map(([key, th]) => (
                      <button key={key} onClick={() => setMeta((m) => ({ ...m, theme: key }))} data-testid={`import-theme-${key}`}
                        className={`rounded-lg border overflow-hidden ${meta.theme === key ? "ring-2 ring-primary" : "border-border"}`}>
                        <div className="h-9" style={{ background: th.bgGradient }} /><div className="text-[11px] py-1">{th.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={create} disabled={busy} className="mt-8 rounded-full h-12 px-8" data-testid="import-create-btn">
                {busy ? <><Loader2 className="animate-spin mr-2" size={18} /> Importing…</> : <>Import {events.length} events <ArrowRight size={16} className="ml-1" /></>}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
